import { InvoiceAction } from '../enums/invoice-action.enum';
import { InvoiceStatus } from '../enums/invoice-status.enum';
import { InvoiceActionRule } from '../models/invoice-action-rule.model';
import { InvoiceBannerType, InvoiceStatusRule } from '../models/invoice-status-rule.model';

// ─── Builders ─────────────────────────────────────────────────────────────────

function action(
  actionType: InvoiceAction,
  label: string,
  icon: string,
  enabled: boolean,
  confirmationMessage?: string,
  errorMessage?: string,
  requiredPermission?: string
): InvoiceActionRule {
  return { action: actionType, label, icon, enabled, confirmationMessage, errorMessage, requiredPermission };
}

function rule(
  label: string,
  badgeStatus: string,
  color: InvoiceStatusRule['color'],
  description: string,
  impact: string,
  actions: readonly InvoiceActionRule[],
  banner: InvoiceBannerType = null,
  dimmed = false,
  pulsing = false
): InvoiceStatusRule {
  return { label, badgeStatus, color, description, impact, actions, banner, dimmed, pulsing };
}

// ─── Shared action sets ────────────────────────────────────────────────────────
// Actions shown: Edit, Resend, Cancel, DownloadDanfe, SendEmail.
// View, DownloadXml, RefreshStatus and Support are not rendered in the card UI.

const issuedActions: readonly InvoiceActionRule[] = [
  action(InvoiceAction.Edit,          'Editar',         'edit',              false),
  action(InvoiceAction.Resend,        'Reemitir',       'arrows-right-left', false),
  action(InvoiceAction.Cancel,        'Cancelar',       'close',                 true,  'Cancelar esta nota fiscal?',  'Erro ao cancelar nota fiscal',  'notaFiscal:cancel'),
  action(InvoiceAction.DownloadDanfe, 'PDF',            'arrow-bar-to-down', true,  undefined,                     'Erro ao baixar nota fiscal'),
  action(InvoiceAction.SendEmail,     'Enviar e-mail',  'mail',              true,  'Reenviar e-mail ao tomador?', 'Erro ao enviar e-mail'),
];

const sentEmailActions: readonly InvoiceActionRule[] = [
  action(InvoiceAction.Edit,          'Editar',         'edit',              false),
  action(InvoiceAction.Resend,        'Reemitir',       'arrows-right-left', false),
  action(InvoiceAction.Cancel,        'Cancelar',       'close',                 false),
];

const failedActions: readonly InvoiceActionRule[] = [
  action(InvoiceAction.Edit,          'Editar',         'edit',              true,  undefined,                      undefined,                       'notaFiscal:edit'),
  action(InvoiceAction.Resend,        'Reemitir',       'arrows-right-left', true,  'Reemitir esta nota fiscal?',   'Erro ao reemitir nota fiscal',  'notaFiscal:resend'),
  action(InvoiceAction.Cancel,        'Cancelar',       'close',                 false),
  action(InvoiceAction.DownloadDanfe, 'PDF',            'arrow-bar-to-down', false),
  action(InvoiceAction.SendEmail,     'Enviar e-mail',  'mail',              false),
];

const errorInternalActions: readonly InvoiceActionRule[] = [
  action(InvoiceAction.Edit,          'Editar',         'edit',              false),
  action(InvoiceAction.Resend,        'Reemitir',       'arrows-right-left', false),
  action(InvoiceAction.Cancel,        'Cancelar',       'close',                 false),
  action(InvoiceAction.DownloadDanfe, 'PDF',            'arrow-bar-to-down', false),
  action(InvoiceAction.SendEmail,     'Enviar e-mail',  'mail',              false),
];

const canceledActions: readonly InvoiceActionRule[] = [
  action(InvoiceAction.Edit,          'Editar',         'edit',              true,  undefined,                      undefined,                       'notaFiscal:edit'),
  action(InvoiceAction.Resend,        'Reemitir',       'arrows-right-left', true,  'Reemitir esta nota fiscal?',   'Erro ao reemitir nota fiscal',  'notaFiscal:resend'),
  action(InvoiceAction.Cancel,        'Cancelar',       'close',                 false),
  action(InvoiceAction.DownloadDanfe, 'PDF',            'arrow-bar-to-down', false),
  action(InvoiceAction.SendEmail,     'Enviar e-mail',  'mail',              false),
];

const pendingActions: readonly InvoiceActionRule[] = [
  action(InvoiceAction.Edit,          'Editar',         'edit',              true,  undefined,                      undefined,                       'notaFiscal:edit'),
  action(InvoiceAction.Resend,        'Reemitir',       'arrows-right-left', true,  'Reemitir esta nota fiscal?',   'Erro ao reemitir nota fiscal',  'notaFiscal:resend'),
  action(InvoiceAction.Cancel,        'Cancelar',       'close',                 true,  'Cancelar esta nota fiscal?',  'Erro ao cancelar nota fiscal',  'notaFiscal:cancel'),
  action(InvoiceAction.DownloadDanfe, 'PDF',            'arrow-bar-to-down', false),
  action(InvoiceAction.SendEmail,     'Enviar e-mail',  'mail',              false),
];

const readOnlyActions: readonly InvoiceActionRule[] = [
  action(InvoiceAction.Edit,          'Editar',         'edit',              false),
  action(InvoiceAction.Resend,        'Reemitir',       'arrows-right-left', false),
  action(InvoiceAction.Cancel,        'Cancelar',       'close',                 false),
  action(InvoiceAction.DownloadDanfe, 'PDF',            'arrow-bar-to-down', false),
  action(InvoiceAction.SendEmail,     'Enviar e-mail',  'mail',              false),
];

const draftActions: readonly InvoiceActionRule[] = [
  action(InvoiceAction.Edit,          'Editar',         'edit',              false),
  action(InvoiceAction.Resend,        'Reemitir',       'arrows-right-left', false),
  action(InvoiceAction.Cancel,        'Cancelar',       'close',                 true,  'Cancelar este registro simbólico?', 'Erro ao cancelar', 'notaFiscal:cancel'),
  action(InvoiceAction.DownloadDanfe, 'PDF',            'arrow-bar-to-down', false),
  action(InvoiceAction.SendEmail,     'Enviar e-mail',  'mail',              false),
];

// ─── Central config ────────────────────────────────────────────────────────────

export const INVOICE_STATUS_ACTION_CONFIG: Record<InvoiceStatus, InvoiceStatusRule> = {
  [InvoiceStatus.Failed]:
    rule('Falha',                'failed',               'danger',    'Falha na emissão da nota.',              'Permite correção e reemissão.',              failedActions,        'error'),
  [InvoiceStatus.RegisterError]:
    rule('Erro no registro',     'register_error',       'danger',    'Erro no registro fiscal interno.',       'Permite revisão e reemissão.',               failedActions,        'error'),
  [InvoiceStatus.Error]:
    rule('Erro interno',         'error',                'danger',    'Erro interno de emissão.',               'Requer contato com suporte técnico.',         errorInternalActions, 'support'),
  [InvoiceStatus.Canceled]:
    rule('Cancelado',            'canceled',             'danger',    'Nota fiscal cancelada.',                 'Permite reemissão corrigida.',               canceledActions,      null, true),
  [InvoiceStatus.Created]:
    rule('Processando',          'created',              'warning',   'Nota em processamento.',                 'Acompanhe a atualização do status.',          readOnlyActions,      null, false, true),
  [InvoiceStatus.Issued]:
    rule('Emitido',              'issued',               'success',   'Nota fiscal emitida com sucesso.',       'Disponível para download e cancelamento.',    issuedActions),
  [InvoiceStatus.SentEmail]:
    rule('Enviado contabilidade','sent_email',           'info',      'Nota enviada por e-mail / contabilidade.','Mantém rastreabilidade do envio.',           sentEmailActions),
  [InvoiceStatus.None]:
    rule('Aguardando emissão',   'none',                 'warning',   'Receita sem nota emitida.',              'Aguarda fluxo de emissão.',                  readOnlyActions),
  [InvoiceStatus.Draft]:
    rule('Registro simbólico',   'draft',                'secondary', 'Nota em rascunho simbólico.',            'Permite cancelamento quando autorizado.',     draftActions),
  [InvoiceStatus.WaitingForSplit]:
    rule('Aguardando divisão',   'waiting_for_split',    'warning',   'Aguardando divisão financeira.',         'Bloqueia a emissão até a divisão.',          readOnlyActions),
  [InvoiceStatus.WaitingCalculateTaxes]:
    rule('Calculando tributos',  'WaitingCalculateTaxes','warning',   'Tributos em cálculo.',                   'Aguarda finalização fiscal.',                readOnlyActions),
  [InvoiceStatus.Unknown]:
    rule('Pendente',             'unknown',              'secondary', 'Status não identificado.',               'Apenas acompanhamento.',                     readOnlyActions),
  [InvoiceStatus.Pending]:
    rule('Pendente',             'pending',              'warning',   'Nota pendente de processamento.',        'Permite edição, reemissão e cancelamento.',    pendingActions),
    
};
